// types
import {
	Account,
	Activity,
	ActivityType,
	Contract,
	Entity,
	Ifr,
	IfrPlan,
	Invoice,
	Project,
	Transaction,
	User,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Avatar, Box, Tooltip, Typography } from '@mui/material';

// ui
import { Time } from '../Time/Time';
import { UserAvatar } from '../UserAvatar/UserAvatar';

// Helper function to find latest activity
const findLatestActivity = (
	activities: Activity[] | number[] | undefined,
	activityTypes: string[]
): Activity | null => {
	if (!activities || activities.length === 0 || typeof activities[0] !== 'object') {
		return null;
	}

	const populatedActivities = activities as Activity[];
	const relevantActivities = populatedActivities.filter((activity) =>
		activityTypes.includes(activity.type)
	);

	if (relevantActivities.length === 0) {
		return null;
	}

	return relevantActivities.sort(
		(a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
	)[0];
};

// props
export interface UserAvatarModifiedProps {
	account?: Account;
	activity?: Activity;
	activityTypes?: ActivityType[];
	contract?: Contract;
	date?: string;
	entity?: Entity;
	hasCreatedFallback?: boolean;
	ifr?: Ifr;
	ifrPlan?: IfrPlan;
	invoice?: Invoice;
	project?: Project;
	transaction?: Transaction;
	user?: User;
	variant?: 'compact' | 'normal';
}

export const UserAvatarModified = ({
	account,
	activity,
	activityTypes,
	contract,
	date,
	entity,
	hasCreatedFallback = false,
	ifr,
	ifrPlan,
	invoice,
	project,
	transaction,
	user,
	variant = 'normal',
}: UserAvatarModifiedProps) => {
	// hooks
	const { t } = useTranslation();

	let actor;
	let actorUser = user;
	let displayDate = date;

	// If activity is directly provided
	if (activity) {
		actor = activity.actor;
		displayDate = activity.created;
	} else {
		// Get resource and validate
		const resource =
			account || contract || entity || ifr || ifrPlan || invoice || project || transaction || user;

		if (resource) {
			// Find latest relevant activity
			if (activityTypes && resource.activities) {
				const latestActivity = findLatestActivity(resource.activities, activityTypes);

				if (latestActivity) {
					actor = latestActivity.actor;
					displayDate = latestActivity.created;
				} else {
					actorUser = undefined;
				}
			}

			if (!displayDate && hasCreatedFallback) {
				displayDate = resource?.created;
			}
		}
	}

	if (actor && typeof actor === 'object') {
		actorUser = actor;
	}

	if (displayDate) {
		const time = <Time date={new Date(displayDate)} TypographyProps={{ variant: 'caption' }} />;

		if (variant === 'compact') {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Tooltip placement="top" title={actorUser?.fullName || t('na')}>
						<Avatar
							src={actorUser?.photo || undefined}
							sx={{
								width: 20,
								height: 20,
							}}
						/>
					</Tooltip>
					<Typography
						variant="caption"
						sx={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							maxWidth: '120px',
						}}>
						{time}
					</Typography>
				</Box>
			);
		}

		return (
			<UserAvatar
				user={actorUser || null}
				caption={{
					children: time,
				}}
			/>
		);
	}

	return <Typography variant={variant === 'compact' ? 'caption' : 'body1'}>{t('na')}</Typography>;
};
